// frontend/src/pages/dashboard/Taskhooks/useTaskData.js
import { useState, useCallback, useEffect } from "react";
import { taskAPI, projectAPI, userAPI, commentAPI, attachmentAPI } from "../../../services/api";
import { toast } from "react-toastify";
import axios from "axios";

export const useTaskData = (authUser, company, searchParams, hasPermission) => {
  const [tasks, setTasks] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [taskStatuses, setTaskStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Pagination State
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
    lastPage: 1
  });

  const fetchTasks = useCallback(async (pageNum = 1) => {
    setLoading(true);
    
    try {
      
      const response = await taskAPI.getAll({
        page: pageNum,
        per_page: 20
      });
      
      // Handle response structure
      const responseData = response.data;
      const tasksData = responseData.data || [];
      const paginationMeta = responseData.meta || {};
      
      // Client-side project filter
      const projectId = searchParams.get("project");
      let filteredTasksData = tasksData;
      if (projectId) {
        filteredTasksData = tasksData.filter(
          (task) => String(task.project_id) === String(projectId)
        );
      }
      
      const enrichedTasks = filteredTasksData.map((t) => ({
        ...t,
        statusObj: t.status || null,
        assignedUsers: t.assigned_users || [],
        attachments: t.attachments || [],
        comment_count: t.comment_count || 0,
        tracked_time: t.tracked_time || {
          formatted: "00h 00m",
          is_running: false,
          total_seconds: 0,
        },
      }));
      
      setTasks(enrichedTasks);
      
      setPagination(prev => ({
        ...prev,
        page: pageNum,
        total: paginationMeta?.total ?? tasksData.length,
        lastPage: paginationMeta?.last_page ?? 1,
      }));
      
    } catch (err) {
      console.error("Error fetching tasks:", err);
      setError("Failed to load tasks");
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [searchParams]);

  // Page Change Handler
  const handlePageChange = useCallback((newPage) => {
    fetchTasks(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [fetchTasks]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await userAPI.getAll();
      const activeMembers = response.data.filter((member) => member.is_active);
      setUsers(
        activeMembers.map((member) => ({
          id: member.user.id,
          name: member.user.name,
          email: member.user.email,
          role: member.role?.name || "N/A",
          avatar: member.user.avatar || null,
        }))
      );
    } catch (err) {
      console.error("Failed to load users:", err);
    }
  }, []);

  const fetchProjects = useCallback(async () => {
    try {
      const response = await projectAPI.getAll();
      const projectsData = response.data?.data || response.data || [];
      setProjects(
        projectsData.map((p) => ({
          ...p,
          assignedUsers: p.assigned_users || [],
        }))
      );
    } catch (err) {
      console.error("Error fetching projects:", err);
    }
  }, []);

  const fetchStatuses = useCallback(async () => {
    try {
      const token = localStorage.getItem("access_token");
      const res = await axios.get(
        `${process.env.REACT_APP_API_URL}/statuses?type=task`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setTaskStatuses(res.data);
    } catch (err) {
      console.error("Failed to load task statuses:", err);
    }
  }, []);

  const loadTaskComments = useCallback(async (taskId) => {
    try {
      const res = await commentAPI.getByTask(taskId);
      return res.data;
    } catch (err) {
      console.error("Failed to load task comments:", err);
      return [];
    }
  }, []);

  const fetchAttachments = useCallback(async (taskId) => {
    if (!taskId) return [];
    try {
      const response = await attachmentAPI.listByTask(taskId);
      return response.data;
    } catch (err) {
      console.error("Error fetching attachments:", err);
      return [];
    }
  }, []);

  // Reset to page 1 when filters change
  useEffect(() => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchTasks(1);
  }, [searchParams]);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      await Promise.all([fetchUsers(), fetchProjects(), fetchStatuses()]);
      await fetchTasks(1);
    };
    loadData();
  }, [fetchUsers, fetchProjects, fetchStatuses, fetchTasks]);

  return {
    tasks,
    setTasks,
    projects,
    users,
    taskStatuses,
    loading,
    error,
    pagination,
    setPagination,
    handlePageChange,
    loadTaskComments,
    fetchAttachments,
  };
};